// src/components/resources/quizzes/components/Quiz.tsx

'use client'

import { useEffect, useMemo } from 'react'
import { useIsomorphicLayoutEffect } from '~/lib/hooks/useIsomorphicLayoutEffect'
import { useQuizState } from '~/components/resources/quizzes/hooks/useQuizState'
import { QuizHeader } from '~/components/resources/quizzes/components/QuizHeader'
import { QuestionDisplay } from '~/components/resources/quizzes/components/QuestionDisplay'
import { Button } from '~/components/ui/Button'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import RiSettings4Line from '~/icons/remix/generated/RiSettings4Line'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import { cn } from '~/lib/utils'
import type { QuizProps } from '~/components/resources/quizzes/types/quiz-types'
import { SectionReview } from '~/components/resources/quizzes/components/SectionReview'
import { QuizSettings } from '~/components/resources/quizzes/components/QuizSettings'
import { QuizCover } from '~/components/resources/quizzes/components/QuizCover'
import { QuestionRenderer } from '~/components/resources/quizzes/components/QuestionRenderer'
import { Navigation } from '~/components/resources/quizzes/components/Navigation'
import { QuizCompletion } from '~/components/resources/quizzes/components/QuizCompletion'
import { FeedbackTiming } from '~/lib/resources/types/tools/quiz'
import type { QuizFeedback, FeedbackSettings } from '~/lib/resources/types/tools/quiz'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'

export function Quiz({ quiz, onComplete }: QuizProps): JSX.Element {
  const {
    state,
    setState,
    handleAnswer,
    toggleHint,
    isAnswerCorrect,
    calculateScore,
    submitAnswer,
    setFeedbackPreferences,
    startSectionReview,
    endSectionReview,
    shouldShowSectionSummary,
    currentSection,
    currentQuestion,
    resetQuiz
  } = useQuizState(quiz)

  // Engagement timers
  // - getTotalMs: total from quiz start to completion
  // - getDeltaMs: per-interaction deltas for granular timing
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  // Add click outside handler for settings
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      // Don't close if clicking the settings button
      if (target.closest('[data-settings-button]')) return
      // Only close if clicking outside both button and panel
      if (!target.closest('[data-settings-panel]')) {
        setState(prev => ({ ...prev, showSettings: false }))
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [setState])

  // Cache Components: the settings panel is transient chrome (gear toggle +
  // click-outside-to-close). <Activity> would otherwise preserve it open, so a
  // user returning to the quiz route would find the panel still expanded over
  // the question. Close it when this route is hidden. (Quiz PROGRESS — current
  // question, answers, score — is intentionally preserved; only this transient
  // panel resets.)
  useIsomorphicLayoutEffect(() => {
    return () => {
      setState(prev => (prev.showSettings ? { ...prev, showSettings: false } : prev))
    }
  }, [setState])

  // Handle feedback changes
  const handleFeedbackChange = (update: {
    timing?: typeof FeedbackTiming[keyof typeof FeedbackTiming]
    settings?: Partial<FeedbackSettings>
  }): void => {
    setFeedbackPreferences({
      timing: update.timing ?? state.feedbackPreferences.timing,
      settings: {
        ...state.feedbackPreferences.settings,
        ...update.settings
      }
    } as QuizFeedback)
  }

  // Send interact for answer selection and submissions with delta timing
  const trackInteract = (interactionType: string, extra?: Record<string, unknown>): void => {
    sendResourceEvent('interact', {
      resourceType: 'quiz',
      resourceSlug: quiz.slug,
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType, ...(extra ?? {}) }
    })
  }

  // Early return for cover screen
  if (!state.started) {
    return (
      <QuizCover
        quiz={quiz}
        onStart={() => {
          handleFeedbackChange({
            settings: {
              showDetailedExplanations: true,
              showVisualIndicators: true
            }
          })
          setState(prev => ({ ...prev, started: true, startedAt: new Date().toISOString() }))
          // Track quiz interaction start
          sendResourceEvent('interact', {
            resourceType: 'quiz',
            resourceSlug: quiz.slug,
            engagementMs: getDeltaMs(),
            params: { interaction_type: 'start' }
          })
        }}
        onOpenSettings={(update) => {
          handleFeedbackChange(update)
          setState(prev => ({ ...prev, showSettings: true }))
        }}
      />
    )
  }

  // Early return if no section or question is loaded
  if (!currentSection || !currentQuestion) {
    return <div>Loading quiz...</div>
  }

  // Single source of truth for finishing the quiz: score it, mark it complete,
  // fire the GA `complete` event, and notify the parent. Every finish path
  // (last-question `handleNext`, section-timing `handleSectionComplete`, and the
  // "View Final Results" button on the section-review screen) MUST go through
  // this — the review-screen copy previously omitted `isComplete: true` and the
  // `complete` event, so section-timing quizzes finished via review silently
  // under-reported completions.
  const completeQuiz = (): void => {
    const result = calculateScore()
    setState(prev => ({
      ...prev,
      result,
      showExplanation: true,
      isComplete: true
    }))
    sendResourceEvent('complete', {
      resourceType: 'quiz',
      resourceSlug: quiz.slug,
      engagementMs: getTotalMs(),
      params: { score: result.score, totalQuestions: result.totalQuestions }
    })
    onComplete?.()
  }

  const handleSectionComplete = (): void => {
    if (state.feedbackPreferences.timing === FeedbackTiming.SECTION) {
      startSectionReview()
    } else {
      // Move to next section directly
      if (state.currentSectionIndex < quiz.sections.length - 1) {
        setState(prev => ({
          ...prev,
          currentSectionIndex: prev.currentSectionIndex + 1,
          currentQuestionIndex: 0,
          showHint: false,
          showExplanation: false
        }))
      } else {
        // Quiz completed
        completeQuiz()
      }
    }
  }

  const handleNext = (): void => {
    if (state.currentQuestionIndex < currentSection.questions.length - 1) {
      // Move to next question
      setState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        showHint: false,
        showExplanation: false
      }))
      trackInteract('next_question', { question_id: currentQuestion.id })
    } else if (state.currentSectionIndex < quiz.sections.length - 1) {
      // Move to next section
      handleSectionComplete()
    } else {
      // Quiz completed
      completeQuiz()
    }
  }

  const handleBack = (): void => {
    if (state.currentQuestionIndex > 0) {
      // Move to previous question
      setState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1,
        showHint: false,
        showExplanation: false
      }))
    } else if (state.currentSectionIndex > 0) {
      // Move to previous section
      const prevSection = quiz.sections[state.currentSectionIndex - 1]
      if (!prevSection) return

      setState(prev => ({
        ...prev,
        currentSectionIndex: prev.currentSectionIndex - 1,
        currentQuestionIndex: prevSection.questions.length - 1,
        showHint: false,
        showExplanation: false
      }))
    }
  }

  const canNavigateBack = state.currentQuestionIndex > 0 || state.currentSectionIndex > 0
  const canNavigateForward = currentQuestion && (
    // For questions with submit buttons, require submission
    (currentQuestion.type === 'text' || currentQuestion.type === 'number' || 
     currentQuestion.type === 'multiselect' || currentQuestion.type === 'ordered')
      ? state.answers[currentQuestion.id] !== undefined && (
        // In immediate mode, require feedback to be shown
        // In delayed mode, just require an answer to be submitted
        state.feedbackPreferences.timing === FeedbackTiming.IMMEDIATE
          ? state.showExplanation || !currentQuestion.explanation
          : true
      )
      : state.answers[currentQuestion.id] !== undefined
  )

  // Get current hints safely
  const currentHints = currentQuestion.hints ?? []
  const showHints = state.showHint && currentHints.length > 0
  const currentHint = showHints ? currentHints[0] : undefined

  // Determine when to show visual indicators and explanations separately
  const shouldShowVisualIndicators = (): boolean => {
    const hasAnswer = state.answers[currentQuestion.id] !== undefined
    if (!hasAnswer) return false

    const requiresSubmission = ['text', 'number', 'multiselect', 'ordered'].includes(currentQuestion.type)
    if (requiresSubmission && !state.showExplanation) return false

    if (state.feedbackPreferences.timing === FeedbackTiming.IMMEDIATE) {
      return state.feedbackPreferences.settings.showVisualIndicators ?? false
    }

    return state.isReviewingSection || state.result !== null
  }

  const shouldShowExplanations = (): boolean => {
    const hasAnswer = state.answers[currentQuestion.id] !== undefined
    if (!hasAnswer) return false

    const requiresSubmission = ['text', 'number', 'multiselect', 'ordered'].includes(currentQuestion.type)
    if (requiresSubmission && !state.showExplanation) return false

    if (state.feedbackPreferences.timing === FeedbackTiming.IMMEDIATE) {
      return state.showExplanation && (state.feedbackPreferences.settings.showDetailedExplanations ?? false)
    }

    return state.isReviewingSection
  }

  // Sr-only page title while the quiz is in progress. The cover screen renders a visible
  // h1 in QuizCover; ResourceLayout uses suppressFallbackHeading so it does not add a
  // second h1. QuizWrapper (embed) has no layout wrapper, so this heading is required there.
  return (
    <div className="relative min-h-screen">
      <h1 className="sr-only">{quiz.title}</h1>
      {quiz.description ? (
        <p className="sr-only">{quiz.description}</p>
      ) : null}
      <div className="mx-auto max-w-4xl px-2 pb-16 pt-4 sm:px-4 sm:pb-24 sm:pt-8 lg:px-6">
        <div className="rounded-xl sm:rounded-2xl bg-white shadow-lg sm:shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
          {/* Header with Settings */}
          <div className="relative px-3 sm:px-6 pt-3 sm:pt-6">
          {!state.result && (
              <>
                <QuizHeader
                  currentSectionIndex={state.currentSectionIndex}
                  currentQuestionIndex={state.currentQuestionIndex}
                  totalSections={quiz.sections.length}
                  questionsInSection={currentSection.questions.length}
                  sectionTitle={currentSection.title}
                  questionsAnswered={Object.keys(state.answers).length}
                  totalQuestions={quiz.sections.reduce(
                    (total, section) => total + section.questions.length,
                    0
                  )}
                />

                {/* Settings Button */}
                <div className="absolute right-3 sm:right-6 top-3 sm:top-6 z-50">
                <button
                  data-settings-button
                  onClick={(e) => {
                    e.stopPropagation()
                    setState(prev => ({ ...prev, showSettings: !prev.showSettings }))
                  }}
                  className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                    state.showSettings
                      ? "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400"
                      : "text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                  )}
                  aria-label="Quiz Settings"
                >
                    <RiSettings4Line className="h-4 w-4" />
                </button>

                  {/* Settings Panel */}
                {state.showSettings && (
                  <div 
                    data-settings-panel
                      className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-2rem)] max-w-[320px] rounded-lg bg-white p-4 shadow-xl ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/5"
                  >
                    <QuizSettings
                      feedback={state.feedbackPreferences}
                      onFeedbackChange={handleFeedbackChange}
                    />
                  </div>
                )}
              </div>
              </>
          )}
          </div>

          {/* Main Content Area */}
          <div className="p-4 sm:p-6">
            {state.result ? (
              <QuizCompletion
                quiz={quiz}
                result={state.result}
                onRetake={resetQuiz}
              />
            ) : state.isReviewingSection ? (
              <>
                <SectionReview
                  section={currentSection}
                  answers={state.answers}
                />
                <div className="mt-8 sm:mt-12 flex justify-center">
                  <button
                    onClick={() => {
                      endSectionReview()
                      if (state.currentSectionIndex < quiz.sections.length - 1) {
                        setState(prev => ({
                          ...prev,
                          currentSectionIndex: prev.currentSectionIndex + 1,
                          currentQuestionIndex: 0,
                          showHint: false,
                          showExplanation: false
                        }))
                      } else {
                        // Finish via the shared path so the section-review
                        // "View Final Results" completion is counted like the
                        // other finish paths (isComplete + `complete` event).
                        completeQuiz()
                      }
                    }}
                    className="inline-flex w-full sm:w-auto items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white shadow-sm hover:from-indigo-500 hover:to-blue-500"
                  >
                    {state.currentSectionIndex + 1 >= quiz.sections.length ? 'View Final Results' : 'Continue to Next Section'}
                    <RiArrowRightLine className="ml-2 h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Section Summary */}
                {shouldShowSectionSummary(state.currentSectionIndex) && (
                  <div className="mb-4 sm:mb-6 rounded-lg bg-gray-50/50 p-4 sm:p-6 ring-1 ring-gray-900/10 dark:bg-gray-800/50 dark:ring-white/10">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
                      <div>
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                          Section Complete!
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          You&apos;ve completed all questions in this section.
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                          {calculateScore().sectionResults[currentSection.id]?.percentage ?? 0}%
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Section Score
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row justify-end gap-3">
                      <Button
                        variant="outline"
                        onClick={startSectionReview}
                        className="w-full sm:w-auto"
                      >
                        Review Answers
                      </Button>
                      {state.currentSectionIndex < quiz.sections.length - 1 && (
                        <Button 
                          onClick={handleSectionComplete}
                          className="w-full sm:w-auto"
                        >
                          Next Section
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Question Area */}
                <div className="space-y-2">
                  <div className="rounded-xl bg-gray-50/50 p-3 sm:p-4 ring-1 ring-gray-900/10 dark:bg-gray-800/50 dark:ring-white/10">
                    <QuestionDisplay
                      question={currentQuestion}
                      questionNumber={state.currentQuestionIndex + 1}
                    />
                    {/* Hint Button & Display */}
                    {state.feedbackPreferences.settings.enableHints && 
                     currentHints.length > 0 && 
                     !state.showExplanation && (
                      <div className="mt-4">
                        <button
                          onClick={toggleHint}
                          className="group flex items-center gap-2.5 text-amber-600 hover:text-amber-500 dark:text-amber-400 dark:hover:text-amber-300"
                        >
                          <div className="flex h-5 items-center justify-center">
                            <RiLightbulbLine className="h-5 w-5 text-amber-500 transition-all group-hover:scale-110 group-hover:rotate-12" />
                          </div>
                          <span className="text-sm font-medium flex items-center">
                            {state.showHint ? 'Hide Hint' : 'Need a hint?'}
                          </span>
                        </button>

                        {state.showHint && currentHint && (
                          <div className="mt-3 overflow-hidden rounded-lg bg-amber-50/50 ring-1 ring-amber-500/10 shadow-sm transition-all dark:bg-amber-900/20 dark:ring-amber-400/10">
                            <div className="border-l-4 border-amber-500 px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                                  Hint
                                </span>
                              </div>
                              <p className="mt-2 text-sm leading-relaxed text-amber-800 dark:text-amber-200">
                                {currentHint}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-6">
                      <QuestionRenderer
                        question={currentQuestion}
                        selectedAnswer={state.answers[currentQuestion.id] ?? []}
                        onAnswerSelect={(answer) => handleAnswer(currentQuestion.id, answer)}
                        showFeedback={shouldShowVisualIndicators()}
                        showExplanations={shouldShowExplanations()}
                        disabled={state.showExplanation && currentQuestion.type !== 'ordered'}
                        isAnswerCorrect={isAnswerCorrect(currentQuestion.id)}
                        feedbackPreferences={state.feedbackPreferences}
                      />
                    </div>

                    {/* Submit Button for Multiple Choice */}
                    {(currentQuestion.type === 'multiselect' || currentQuestion.type === 'ordered') && 
                     state.answers[currentQuestion.id] && (
                      <div className="mt-6 flex justify-end">
                        <Button
                          onClick={() => submitAnswer(currentQuestion.id)}
                          className={cn(
                            "w-full sm:w-auto px-6 inline-flex items-center justify-center gap-2",
                            state.showExplanation && "bg-green-600 hover:bg-green-500"
                          )}
                        >
                          {state.showExplanation ? (
                            <>
                              <RiCheckLine className="h-4 w-4" />
                              Submitted
                            </>
                          ) : (
                            'Submit'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Navigation */}
                  <Navigation
                    canNavigateBack={canNavigateBack}
                    canNavigateForward={canNavigateForward}
                    onBack={handleBack}
                    onNext={handleNext}
                    className="pt-4 sm:pt-4"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}